import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, LineChartIcon, TrendingUp, Shuffle, Clock, Server, ArrowRightLeft } from 'lucide-react';
import { Button } from '../ui/Button';

interface StrategyCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  tags: string[];
  level: 'beginner' | 'intermediate' | 'advanced';
}

const strategies: StrategyCard[] = [
  {
    title: 'Grid Trading',
    description: 'Place buy and sell orders at predefined intervals to profit from price oscillations in sideways markets.',
    icon: <ArrowRightLeft className="w-12 h-12 text-blue-500" />,
    tags: ['Sideways Market', 'Automated', 'Low Risk'],
    level: 'beginner'
  },
  {
    title: 'Trend Following',
    description: 'Identify and follow existing market trends using moving averages and momentum indicators.',
    icon: <TrendingUp className="w-12 h-12 text-purple-500" />,
    tags: ['Trending Market', 'Technical', 'Medium Risk'],
    level: 'intermediate'
  },
  {
    title: 'Arbitrage',
    description: 'Exploit price discrepancies between different exchanges by buying low and selling high simultaneously.',
    icon: <Shuffle className="w-12 h-12 text-pink-500" />,
    tags: ['Multi-Exchange', 'Speed', 'Low Risk'],
    level: 'intermediate'
  },
  {
    title: 'Mean Reversion',
    description: 'Trade on the assumption that prices will revert to their historical average over time.',
    icon: <LineChartIcon className="w-12 h-12 text-blue-500" />,
    tags: ['Statistical', 'Technical', 'Medium Risk'],
    level: 'intermediate'
  },
  {
    title: 'MACD Crossover',
    description: 'Generate buy/sell signals based on Moving Average Convergence Divergence indicator crossovers.',
    icon: <LineChart className="w-12 h-12 text-purple-500" />,
    tags: ['Technical', 'Indicator', 'Medium Risk'],
    level: 'beginner'
  },
  {
    title: 'Dollar Cost Averaging',
    description: 'Automatically invest fixed amounts at regular intervals, regardless of market conditions.',
    icon: <Clock className="w-12 h-12 text-pink-500" />,
    tags: ['Passive', 'Long-term', 'Low Risk'],
    level: 'beginner'
  },
  {
    title: 'News Sentiment',
    description: 'Trade based on real-time analysis of news and social media sentiment about specific cryptocurrencies.',
    icon: <Server className="w-12 h-12 text-blue-500" />,
    tags: ['AI', 'Sentiment', 'High Risk'],
    level: 'advanced'
  },
];

export const StrategiesPage: React.FC = () => {
  const [filter, setFilter] = React.useState<'all' | 'beginner' | 'intermediate' | 'advanced'>('all');
  
  const filteredStrategies = filter === 'all' 
    ? strategies 
    : strategies.filter(strategy => strategy.level === filter);

  return (
    <div className="pt-20">
      <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white py-16">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl md:text-5xl font-bold mb-4">Trading Strategys</h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
              Browse our collection of pre-built trading strategies for your bots, or create custom ones to fit your specific needs.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="flex justify-center mb-10">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-2 inline-flex">
            <button 
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                filter === 'all' ? 'bg-blue-500 text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button 
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                filter === 'beginner' ? 'bg-blue-500 text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              onClick={() => setFilter('beginner')}
            >
              Beginner
            </button>
            <button 
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                filter === 'intermediate' ? 'bg-blue-500 text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              onClick={() => setFilter('intermediate')}
            >
              Intermediate
            </button>
            <button 
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                filter === 'advanced' ? 'bg-blue-500 text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              onClick={() => setFilter('advanced')}
            >
              Advanced
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredStrategies.map((strategy, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-gray-200 dark:border-gray-700"
            >
              <div className="p-6">
                <div className="mb-4">
                  {strategy.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">{strategy.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{strategy.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-6">
                  {strategy.tags.map((tag, tagIndex) => (
                    <span 
                      key={tagIndex} 
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    strategy.level === 'beginner' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                      : strategy.level === 'intermediate'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {strategy.level.charAt(0).toUpperCase() + strategy.level.slice(1)}
                  </span>
                  
                  <Button variant="primary" size="sm">
                    Use Strategy
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};